import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ArrowRight, ArrowDown, Target } from 'lucide-react';

const arr = (x) => (Array.isArray(x) ? x : []);
const num = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
const Box = ({ title, items, tone = 'slate', testid }) => (
  <div className={`rounded-lg border p-3 iad-tone-${tone}`} data-testid={testid}>
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider opacity-80">{title}</p>
    <ul className="space-y-1 text-sm">{arr(items).map((t, i) => <li key={i} className="leading-snug">• {String(t)}</li>)}</ul>
    {!arr(items).length && <p className="text-xs opacity-50">Sin elementos</p>}
  </div>
);

const Ishikawa = ({ v }) => {
  const cats = arr(v.categories);
  const half = Math.ceil(cats.length / 2);
  const Row = ({ list, top }) => (
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.max(half, 1)}, minmax(0, 1fr))` }}>
      {list.map((c, i) => (
        <div key={i} className={`relative flex flex-col ${top ? 'pb-6' : 'pt-6'}`}>
          {!top && <span className="iad-bone iad-bone-up" />}
          <div className="rounded-md border border-amber-400/30 bg-amber-400/5 p-2">
            <p className="text-xs font-semibold text-amber-300">{c.name}</p>
            {arr(c.causes).map((x, j) => <p key={j} className="mt-1 text-xs leading-snug text-slate-300">– {x}</p>)}
          </div>
          {top && <span className="iad-bone iad-bone-down" />}
        </div>
      ))}
    </div>
  );
  return (
    <div className="flex items-stretch gap-3" data-testid="iad-visual-ishikawa">
      <div className="flex-1">
        <Row list={cats.slice(0, half)} top />
        <div className="iad-spine" />
        <Row list={cats.slice(half)} />
      </div>
      <div className="flex w-40 shrink-0 items-center">
        <div className="w-full rounded-lg border-2 border-red-400/60 bg-red-500/10 p-3 text-center text-sm font-semibold text-red-200">{v.effect || 'Efecto'}</div>
      </div>
    </div>
  );
};

const FiveWhys = ({ v }) => (
  <div className="space-y-2" data-testid="iad-visual-five_whys">
    <div className="rounded-md bg-slate-700/40 p-2 text-sm text-slate-200"><b>Síntoma:</b> {v.symptom}</div>
    {arr(v.whys).map((w, i) => (
      <div key={i} className="flex items-start gap-2 pl-3">
        <ArrowDown className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
        <p className="text-sm text-slate-300"><span className="font-semibold text-sky-300">¿Por qué {i + 1}?</span> {w}</p>
      </div>
    ))}
    <div className="rounded-md border border-emerald-400/40 bg-emerald-500/10 p-2 text-sm text-emerald-200"><Target className="mr-1 inline h-4 w-4" /><b>Causa raíz:</b> {v.root_cause}</div>
  </div>
);

const Pareto = ({ v }) => {
  const items = arr(v.items).map((i) => ({ name: String(i.name), value: num(i.value) })).sort((a, b) => b.value - a.value);
  const total = items.reduce((s, i) => s + i.value, 0) || 1;
  let acc = 0;
  const data = items.map((i) => { acc += i.value; return { ...i, cum: Math.round((acc / total) * 1000) / 10 }; });
  return (
    <div className="h-64" data-testid="iad-visual-pareto">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
          <YAxis yAxisId="l" tick={{ fill: '#94a3b8', fontSize: 11 }} />
          <YAxis yAxisId="r" orientation="right" domain={[0, 100]} unit="%" tick={{ fill: '#94a3b8', fontSize: 11 }} />
          <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
          <Bar yAxisId="l" dataKey="value" name={v.unit || 'Valor'} fill="#f59e0b" radius={[4, 4, 0, 0]} />
          <Line yAxisId="r" dataKey="cum" name="% acumulado" stroke="#38bdf8" strokeWidth={2} dot />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const Grid2 = ({ boxes, testid }) => (
  <div className="grid grid-cols-2 gap-2" data-testid={testid}>{boxes.map((b) => <Box key={b.title} {...b} />)}</div>
);

const Covey = ({ v }) => {
  const q = v.quadrants || {};
  return <Grid2 testid="iad-visual-covey" boxes={[
    { title: 'Q1 · Urgente e importante — Hacer ya', items: q.q1, tone: 'red' },
    { title: 'Q2 · Importante, no urgente — Planificar', items: q.q2, tone: 'emerald' },
    { title: 'Q3 · Urgente, no importante — Delegar', items: q.q3, tone: 'amber' },
    { title: 'Q4 · Ni urgente ni importante — Eliminar', items: q.q4, tone: 'slate' }]} />;
};

const Swot = ({ v }) => <Grid2 testid="iad-visual-swot" boxes={[
  { title: 'Fortalezas', items: v.strengths, tone: 'emerald' }, { title: 'Debilidades', items: v.weaknesses, tone: 'red' },
  { title: 'Oportunidades', items: v.opportunities, tone: 'sky' }, { title: 'Amenazas', items: v.threats, tone: 'amber' }]} />;

const Pdca = ({ v }) => (
  <div className="grid grid-cols-4 gap-2" data-testid="iad-visual-pdca">
    {[['Plan', v.plan, 'sky'], ['Do', v.do, 'emerald'], ['Check', v.check, 'amber'], ['Act', v.act, 'violet']].map(([t, i, tone]) => <Box key={t} title={t} items={i} tone={tone} />)}
  </div>
);

const WeightedMatrix = ({ v }) => {
  const criteria = arr(v.criteria);
  const alts = arr(v.alternatives).map((a) => ({ ...a, total: a.total ?? arr(a.scores).reduce((s, x, i) => s + num(x) * num(criteria[i]?.weight), 0) }));
  const best = Math.max(...alts.map((a) => num(a.total)));
  return (
    <div className="overflow-x-auto" data-testid="iad-visual-weighted_matrix">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-400"><th className="p-2">Alternativa</th>
          {criteria.map((c, i) => <th key={i} className="p-2">{c.name} <span className="text-amber-300">({Math.round(num(c.weight) * 100)}%)</span></th>)}
          <th className="p-2">Total</th></tr></thead>
        <tbody>{alts.map((a, i) => (
          <tr key={i} className={`border-t border-slate-700 ${num(a.total) === best ? 'bg-emerald-500/10 text-emerald-200' : 'text-slate-300'}`}>
            <td className="p-2 font-medium">{a.name}</td>{arr(a.scores).map((s, j) => <td key={j} className="p-2">{s}</td>)}
            <td className="p-2 font-bold">{num(a.total).toFixed(2)}</td></tr>))}</tbody>
      </table>
    </div>
  );
};

const SEV = { alta: 'border-red-400/50 text-red-300', media: 'border-amber-400/50 text-amber-300', baja: 'border-emerald-400/50 text-emerald-300' };
const TransformationMap = ({ v }) => (
  <div className="space-y-2" data-testid="iad-visual-transformation_map">
    <div className="rounded-md border border-sky-400/40 bg-sky-500/10 p-2 text-sm font-semibold text-sky-200">Decisión: {v.decision}</div>
    {arr(v.impacts).map((i, k) => (
      <div key={k} className="grid grid-cols-[140px_1fr_1fr] items-start gap-2 rounded-md border border-slate-700 p-2 text-sm">
        <span className={`rounded border px-2 py-0.5 text-center text-xs font-semibold ${SEV[i.severity] || 'border-slate-500 text-slate-300'}`}>{i.area}</span>
        <p className="text-slate-300"><ArrowRight className="mr-1 inline h-3 w-3 text-sky-400" />{i.direct_effect}</p>
        <div className="flex flex-wrap gap-1">{arr(i.cross_effects).map((c, j) => <span key={j} className="rounded bg-slate-700/60 px-2 py-0.5 text-xs text-slate-300">{c}</span>)}</div>
      </div>
    ))}
  </div>
);

const Porter = ({ v }) => (
  <div className="space-y-2" data-testid="iad-visual-porter">{arr(v.forces).map((f, i) => (
    <div key={i} className="grid grid-cols-[180px_1fr] items-center gap-3 text-sm">
      <span className="text-slate-300">{f.name}</span>
      <div><div className="h-2 rounded bg-slate-700"><div className="h-2 rounded bg-amber-400" style={{ width: `${Math.min(num(f.intensity), 5) * 20}%` }} /></div>
        {f.notes && <p className="mt-1 text-xs text-slate-400">{f.notes}</p>}</div>
    </div>))}
  </div>
);

const Bcg = ({ v }) => {
  const by = (q) => arr(v.items).filter((i) => String(i.quadrant).toLowerCase() === q).map((i) => i.name);
  return <Grid2 testid="iad-visual-bcg" boxes={[
    { title: 'Interrogante', items: by('interrogante'), tone: 'amber' }, { title: 'Estrella', items: by('estrella'), tone: 'emerald' },
    { title: 'Perro', items: by('perro'), tone: 'red' }, { title: 'Vaca', items: by('vaca'), tone: 'sky' }]} />;
};

const Columns = ({ cols, testid }) => (
  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4" data-testid={testid}>{cols}</div>
);
const Bsc = ({ v }) => <Columns testid="iad-visual-balanced_scorecard" cols={arr(v.perspectives).map((p, i) => (
  <Box key={i} title={p.name} tone="sky" items={arr(p.objectives).map((o) => `${o.objective} · ${o.kpi} → ${o.target}`)} />))} />;
const Pestel = ({ v }) => <Columns testid="iad-visual-pestel" cols={arr(v.factors).map((f, i) => (
  <Box key={i} title={`${f.dimension} · impacto ${f.impact || '-'}`} tone={f.impact === 'alto' ? 'red' : f.impact === 'medio' ? 'amber' : 'slate'} items={f.items} />))} />;
const Scenarios = ({ v }) => <Columns testid="iad-visual-scenarios" cols={arr(v.scenarios).map((s, i) => (
  <div key={i} className="rounded-lg border border-slate-700 p-3 text-sm">
    <p className="font-semibold text-slate-200">{s.name} <span className="text-sky-300">{Math.round(num(s.probability) * (num(s.probability) <= 1 ? 100 : 1))}%</span></p>
    <p className="mt-1 text-slate-400">{s.description}</p><p className="mt-1 text-amber-300">{s.impact}</p>
  </div>))} />;
const DecisionTree = ({ v }) => (
  <div className="space-y-2" data-testid="iad-visual-decision_tree">
    <div className="rounded-md bg-slate-700/40 p-2 text-sm font-semibold text-slate-200">{v.decision}</div>
    {arr(v.options).map((o, i) => (
      <div key={i} className="ml-6 flex items-center gap-2 border-l-2 border-sky-500/50 pl-3 text-sm text-slate-300">
        <b className="text-sky-300">{o.name}</b> · p={o.probability} · {o.outcome} · <span className="text-amber-300">valor {o.value}</span>
      </div>))}
  </div>
);
const Generic = ({ v }) => <pre className="max-h-64 overflow-auto rounded bg-slate-900 p-2 text-xs text-slate-400" data-testid="iad-visual-generic">{JSON.stringify(v, null, 2)}</pre>;

const RENDERERS = { ishikawa: Ishikawa, five_whys: FiveWhys, pareto: Pareto, covey: Covey, swot: Swot, pdca: Pdca,
  weighted_matrix: WeightedMatrix, transformation_map: TransformationMap, porter: Porter, bcg: Bcg,
  balanced_scorecard: Bsc, pestel: Pestel, scenarios: Scenarios, decision_tree: DecisionTree };

export const IADVisual = ({ type, visual }) => {
  if (!visual || typeof visual !== 'object') return null;
  const R = RENDERERS[type] || Generic;
  return <R v={visual} />;
};
